import React, { useState, useEffect, useRef } from "react";
import Button from "../Button";
import { fetchJson, post } from "../../lib/Fetching";
import moment from "moment";
import TemplateOpenDialog from "./TemplateOpenDialog";
import TemplateSaveDialog from "./TemplateSaveDialog";
import TemplateHelpDialog from "./TemplateHelpDialog";

export default function SearchTemplateManager({ formKey, getCurrentParams, applyTemplate, isClientView }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingForSave, setLoadingForSave] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [overwriteId, setOverwriteId] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [saveSearchFilter, setSaveSearchFilter] = useState("");

  const nameInputRef = useRef(null);
  
  if (isClientView) {
    return null;
  }

  const load = () => {
    setLoading(true);
    fetchJson("/common/search_templates", { form_key: formKey })
      .then((json) => {
        setTemplates(json.templates || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const loadForSave = () => {
    setLoadingForSave(true);
    fetchJson("/common/search_templates", { form_key: formKey })
      .then((json) => {
        setTemplates(json.templates || []);
        setLoadingForSave(false);
      })
      .catch(() => setLoadingForSave(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    if (showSaveDialog) loadForSave();
  }, [showSaveDialog]);

  useEffect(() => {
    if (showSaveDialog && nameInputRef && nameInputRef.current) {
      try { nameInputRef.current.focus(); } catch (e) {}
    }
  }, [showSaveDialog]);

  const makeRelativeDates = (payload) => {
    const p = { ...payload };
    const today = moment().startOf("day");
    const start = p.start_date ? moment(p.start_date, "YYYY-MM-DD") : null;
    const end = p.end_date ? moment(p.end_date, "YYYY-MM-DD") : null;
    if (start || end) {
      const start_days_ago = start ? today.diff(start, "days") : null;
      const end_days_ago = end ? today.diff(end, "days") : null;
      p.relative_date_range = { start_days_ago, end_days_ago };
      delete p.start_date;
      delete p.end_date;
    }
    return p;
  };

  const performSave = () => {
    const name = saveName && saveName.trim();
    if (!name && !overwriteId) return;
    const raw = getCurrentParams ? getCurrentParams() : {};
    const payload = makeRelativeDates(raw);
    setSaving(true);
    const done = () => {
      setSaving(false);
      setShowSaveDialog(false);
      setOverwriteId(null);
      setSaveName("");
      setOpen(true);
      load();
    };
    if (overwriteId) {
      post(`/common/search_templates/${overwriteId}`, { name: name || undefined, payload }, "PUT").then(done).catch(() => setSaving(false));
    } else {
      post("/common/search_templates", { form_key: formKey, name, payload }).then(done).catch(() => setSaving(false));
    }
  };

  const onApply = (tpl) => {
    const payload = { ...(tpl.payload || {}) };
    const rel = payload.relative_date_range;
    if (rel && (rel.start_days_ago != null || rel.end_days_ago != null)) {
      const today = moment().startOf("day");
      payload.start_date = rel.start_days_ago != null ? today.clone().subtract(rel.start_days_ago, "days").format("YYYY-MM-DD") : null;
      payload.end_date = rel.end_days_ago != null ? today.clone().subtract(rel.end_days_ago, "days").format("YYYY-MM-DD") : null;
    }
    applyTemplate && applyTemplate(payload);
  };

  const favourite = (tpl) => {
    post(`/common/search_templates/${tpl.id}/favourite`, {})
      .then(load)
      .catch(() => {});
  };

  const unfavourite = (tpl) => {
    post(`/common/search_templates/${tpl.id}/unfavourite`, {}, "DELETE")
      .then(load)
      .catch(() => {});
  };

  const destroyTemplate = (tpl) => {
    if (!window.confirm("Delete this template?")) return;
    post(`/common/search_templates/${tpl.id}`, {}, "DELETE")
      .then(load)
      .catch(() => {});
  };


  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <div className="btn-group">
        <Button
          icon="fa-question"
          onClick={() => {
            setOpen(false);
            setShowSaveDialog(false);
            setShowHelpDialog(!showHelpDialog);
          }}
        />
        <Button
          title={saving ? (I18n && I18n.t ? I18n.t("templates.saving") : "Saving...") : (I18n && I18n.t ? I18n.t("templates.save_template") : "Save Template")}
          icon={saving ? "fa-spin fa-spinner" : "fa-star"}
          onClick={() => {
            setOpen(false);
            setShowHelpDialog(false);
            setShowSaveDialog(!showSaveDialog);
          }}
          disabled={saving}
        />
        <Button
          title={loading ? (I18n && I18n.t ? I18n.t("templates.loading") : "Loading...") : (I18n && I18n.t ? I18n.t("templates.load_template") : "Load Template")}
          icon={loading ? "fa-spin fa-spinner" : "fa-folder-open"}
          onClick={() => {
            setShowSaveDialog(false);
            setShowHelpDialog(false);
            setOpen(!open);
          }}
        />
      </div>
      <TemplateOpenDialog
        open={open}
        onClose={() => setOpen(false)}
        templates={templates}
        loading={loading}
        searchFilter={searchFilter}
        onSearchFilterChange={setSearchFilter}
        onApply={onApply}
        onFavourite={favourite}
        onUnfavourite={unfavourite}
        onDestroy={destroyTemplate}
      />
      <TemplateSaveDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        templates={templates}
        saving={saving}
        saveName={saveName}
        onSaveNameChange={setSaveName}
        overwriteId={overwriteId}
        onOverwriteIdChange={setOverwriteId}
        saveSearchFilter={saveSearchFilter}
        onSaveSearchFilterChange={setSaveSearchFilter}
        onSave={performSave}
        nameInputRef={nameInputRef}
      />
      <TemplateHelpDialog
        open={showHelpDialog}
        onClose={() => setShowHelpDialog(false)}
      />
    </span>
  );
}


